"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ShoppingBag, Trash2 } from "lucide-react";
import { formatPrice } from "@/lib/utils";
import { addToCart, removeFromCart, updateCartQuantity } from "@/lib/cart";
import { useValidatedCart } from "@/hooks/useValidatedCart";
import {
  CART_LINE_PROBLEM_LABELS,
  type ValidatedCartLine,
} from "@/lib/cart-validation";
import {
  amountUntilFreeShipping,
  calcDeliveryFee,
  resolveCartShipping,
} from "@/lib/shipping";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";

// 삭제 취소 배너를 자동으로 닫기까지의 시간. ProductPurchasePanel.tsx의
// "장바구니에 담았습니다" 배너와 같은 길이로 맞춘다.
const UNDO_REMOVE_TIMEOUT_MILLISECONDS = 3000;

interface Props {
  siteDeliveryFee: number | null;
  siteFreeDeliveryAt: number | null;
}

interface RemovedLine {
  productId: string;
  optionId: string | null;
  quantity: number;
  // 삭제 시점에 이름을 몰랐던 줄(not_found 등)이면 일반적인 표현으로 대체.
  displayName: string;
}

// 받침 유무로 "을"/"를" 조사를 고른다. 한글 음절이 아닌 문자(영문/숫자 등)로
// 끝나면 관례적으로 "를"을 붙인다.
function objectParticle(text: string): "을" | "를" {
  const lastChar = text.trim().at(-1);
  if (!lastChar) return "를";
  const HANGUL_SYLLABLE_START = 0xac00;
  const HANGUL_SYLLABLE_END = 0xd7a3;
  const code = lastChar.codePointAt(0) ?? 0;
  if (code < HANGUL_SYLLABLE_START || code > HANGUL_SYLLABLE_END) return "를";
  const hasFinalConsonant = (code - HANGUL_SYLLABLE_START) % 28 !== 0;
  return hasFinalConsonant ? "을" : "를";
}

export function CartClient({ siteDeliveryFee, siteFreeDeliveryAt }: Props) {
  const router = useRouter();
  const { loading, lines, reload } = useValidatedCart();

  const [removedLine, setRemovedLine] = useState<RemovedLine | null>(null);
  const undoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
    };
  }, []);

  function handleRemove(line: ValidatedCartLine) {
    removeFromCart(line.cartLine.productId, line.cartLine.optionId);
    setRemovedLine({
      productId: line.cartLine.productId,
      optionId: line.cartLine.optionId,
      quantity: line.cartLine.quantity,
      displayName: line.product?.name ?? "삭제한 상품",
    });
    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
    undoTimeoutRef.current = setTimeout(
      () => setRemovedLine(null),
      UNDO_REMOVE_TIMEOUT_MILLISECONDS,
    );
  }

  function undoRemove() {
    if (!removedLine) return;
    addToCart(
      removedLine.productId,
      removedLine.optionId,
      removedLine.quantity,
    );
    setRemovedLine(null);
    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
  }

  const isEmpty = !loading && lines.length === 0;
  const hasBlockingProblem = lines.some((line) => line.problem !== null);

  const subtotal = lines.reduce(
    (sum, line) => sum + line.unitPrice * line.cartLine.quantity,
    0,
  );
  const shippingProducts = lines
    .map((line) => line.product)
    .filter((product) => product !== null);
  const shippingRule = resolveCartShipping(shippingProducts, {
    deliveryFee: siteDeliveryFee,
    freeDeliveryAt: siteFreeDeliveryAt,
  });
  const deliveryFee = calcDeliveryFee(subtotal, shippingRule);
  const total = subtotal + deliveryFee;
  const nudgeAmount = amountUntilFreeShipping(
    subtotal,
    shippingRule.freeDeliveryAt,
  );

  function goToCheckout() {
    if (hasBlockingProblem || lines.length === 0) return;
    router.push("/order");
  }

  if (loading) {
    return (
      <div className="container section-padding">
        <div className="section-title">
          <h1 className="text-3xl">장바구니</h1>
          <span className="gold-line" />
        </div>
        <div className="mt-8 space-y-4">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="container section-padding">
        <EmptyState
          icon={<ShoppingBag className="w-12 h-12" aria-hidden />}
          title="장바구니가 비어 있습니다"
          description="마음에 드는 상품을 담아보세요."
          action={
            <Link href="/products" className="btn-primary inline-flex">
              상품 보러가기
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="container section-padding">
      <div className="section-title">
        <h1 className="text-3xl">장바구니</h1>
        <span className="gold-line" />
      </div>

      {removedLine && (
        <div
          role="status"
          className="mt-6 flex items-center justify-between gap-3 text-sm px-4 py-3 rounded-lg"
          style={{ backgroundColor: "#DCFCE7", color: "var(--color-success)" }}
        >
          <span>
            {removedLine.displayName}
            {objectParticle(removedLine.displayName)} 삭제했습니다
          </span>
          <button
            type="button"
            onClick={undoRemove}
            className="font-semibold underline shrink-0"
          >
            실행 취소
          </button>
        </div>
      )}

      <div className="mt-8 flex flex-col lg:flex-row gap-8">
        {/* 왼쪽: 장바구니 목록 */}
        <div className="flex-1 lg:w-0 space-y-4">
          {lines.map((line) => (
            <CartLineRow
              key={`${line.cartLine.productId}:${line.cartLine.optionId ?? ""}`}
              line={line}
              onRemove={handleRemove}
              onRetry={reload}
            />
          ))}
        </div>

        {/* 오른쪽: 주문 요약 */}
        <div className="lg:w-80 xl:w-96 flex-shrink-0">
          <div className="card p-6 lg:sticky lg:top-6 space-y-4">
            <h2 className="text-xl font-bold text-primary">주문 요약</h2>

            {hasBlockingProblem && (
              <p className="text-sm text-error">
                문제가 있는 상품이 있습니다. 먼저 해결해 주세요.
              </p>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-light">상품 금액</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-light">배송비</span>
                <span>
                  {deliveryFee === 0 ? (
                    <span className="text-success">무료</span>
                  ) : (
                    formatPrice(deliveryFee)
                  )}
                </span>
              </div>
              {nudgeAmount !== null && subtotal > 0 && (
                <p className="text-xs text-gold-dark">
                  {formatPrice(nudgeAmount)} 더 담으면 무료배송!
                </p>
              )}
            </div>

            <div className="pt-3 border-t border-(--color-border) flex justify-between items-center">
              <span className="font-bold text-primary">총 결제금액</span>
              <span className="text-xl font-bold text-primary">
                {formatPrice(total)}
              </span>
            </div>

            <button
              type="button"
              onClick={goToCheckout}
              disabled={hasBlockingProblem}
              className="btn-gold w-full text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              주문하기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 상품 상세의 수량 버튼(ProductPurchasePanel.tsx)과 같은 48px 터치 영역.
// globals.css가 모든 .btn-*/.input에 min-height: 48px를 강제하는 것과
// 같은 기준으로, 이 사이트의 실제 손님층(연로하신 분들이 많고 휴대폰
// 사용 비중이 큼)에게는 여기서도 줄이지 않는다.
const stepButtonClass =
  "w-12 h-12 flex items-center justify-center rounded-lg border-2 border-(--color-border) text-lg font-bold text-primary transition-colors hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed";

// 문제 배너 안의 해결 버튼(최소 수량으로 변경/재고만큼 변경/다시 시도/삭제).
// 밑줄 텍스트 대신 사이트에 이미 있는 .btn-outline을 그대로 쓰고 크기만
// 좁혀, 이 배너 안에서 가장 눈에 띄는 다음 행동으로 보이게 한다(다른 스타일을
// 새로 만들지 않는다 — OrderForm.tsx의 "주소 찾기" 버튼과 같은 방식).
const recoveryButtonClass = "btn-outline px-3 text-xs";

interface CartLineRowProps {
  line: ValidatedCartLine;
  onRemove: (line: ValidatedCartLine) => void;
  onRetry: () => void;
}

// 한 줄(상품+옵션+수량). 정상이면 수량 조절 UI를, 문제가 있으면 무엇이
// 문제인지와 해결 버튼(가능한 경우)을 보여준다. 장바구니 조작 함수(cart.ts)는
// 로컬스토리지만 바꾸고, useCartLines()가 useSyncExternalStore로 구독 중이라
// 별도 상태 갱신 없이 화면이 자동으로 다시 그려진다.
function CartLineRow({ line, onRemove, onRetry }: CartLineRowProps) {
  const { cartLine, product, option, problem, unitPrice } = line;

  const displayName = product?.name ?? "상품 정보를 불러올 수 없습니다";
  const displayUnitLabel =
    option?.label ?? (product && !product.hasOptions ? product.unit : "");
  const image = product?.images[0];
  const minOrderQuantity = product?.minOrderQuantity ?? 1;
  const stockCap = product?.isPreorder
    ? 99
    : (option?.stock ?? product?.stock ?? 0);

  function setQuantity(quantity: number) {
    updateCartQuantity(cartLine.productId, cartLine.optionId, quantity);
  }

  return (
    <div className="card p-4 flex gap-4">
      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gradient-to-br from-primary to-primary-light flex items-center justify-center flex-shrink-0">
        {image ? (
          // 작은 썸네일은 OrderForm.tsx의 주문 요약과 동일하게 next/image를
          // 쓰지 않는다(no-img-element는 이 저장소에서 warn로 낮춰져 있음).
          <img
            src={image}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-2xl" aria-hidden>
            🎃
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {product ? (
              <Link
                href={`/products/${product.id}`}
                className="font-semibold text-text-dark text-sm leading-snug line-clamp-2 hover:text-primary"
              >
                {displayName}
              </Link>
            ) : (
              <p className="font-semibold text-text-dark text-sm leading-snug line-clamp-2">
                {displayName}
              </p>
            )}
            {displayUnitLabel && (
              <p className="text-xs text-text-light mt-0.5">
                {displayUnitLabel}
              </p>
            )}
          </div>
          {/* 44px 이상의 실제 탭 영역(아이콘 자체는 16px). 삭제는 되돌리기가
              있어(아래 undo 배너) 확인 대화상자 없이 바로 실행한다 — 매번
              한 번 더 누르게 하는 것보다, 탭 영역을 넉넉히 주고 실수해도
              되돌릴 수 있게 하는 쪽이 낫다. */}
          <button
            type="button"
            onClick={() => onRemove(line)}
            className="w-11 h-11 flex items-center justify-center rounded-lg text-text-muted hover:text-error hover:bg-cream-dark transition-colors flex-shrink-0"
            aria-label={`${displayName} 삭제`}
          >
            <Trash2 className="w-4 h-4" aria-hidden />
          </button>
        </div>

        {problem ? (
          <div className="mt-1 p-3 rounded-lg bg-[#FEE2E2] text-error text-xs space-y-2">
            <p>{CART_LINE_PROBLEM_LABELS[problem]}</p>
            <div className="flex flex-wrap gap-2">
              {problem === "unverified" && (
                <button
                  type="button"
                  onClick={onRetry}
                  className={recoveryButtonClass}
                >
                  다시 시도
                </button>
              )}
              {problem === "below_minimum" && (
                <button
                  type="button"
                  onClick={() => setQuantity(minOrderQuantity)}
                  className={recoveryButtonClass}
                >
                  최소 {minOrderQuantity}개로 변경
                </button>
              )}
              {problem === "out_of_stock" && stockCap > 0 && (
                <button
                  type="button"
                  onClick={() => setQuantity(stockCap)}
                  className={recoveryButtonClass}
                >
                  재고 {stockCap}개로 변경
                </button>
              )}
              <button
                type="button"
                onClick={() => onRemove(line)}
                className={recoveryButtonClass}
              >
                삭제
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setQuantity(Math.max(minOrderQuantity, cartLine.quantity - 1))
                }
                disabled={cartLine.quantity <= minOrderQuantity}
                className={stepButtonClass}
                aria-label={`${displayName} 수량 감소`}
              >
                −
              </button>
              <span
                className="w-8 text-center text-sm font-bold"
                aria-live="polite"
                aria-atomic="true"
              >
                {cartLine.quantity}
              </span>
              <button
                type="button"
                onClick={() =>
                  setQuantity(Math.min(stockCap, cartLine.quantity + 1))
                }
                disabled={cartLine.quantity >= stockCap}
                className={stepButtonClass}
                aria-label={`${displayName} 수량 증가`}
              >
                +
              </button>
            </div>
            <span className="font-bold text-primary text-sm">
              {formatPrice(unitPrice * cartLine.quantity)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
